import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CustomIcon } from "../../UI";
import ToggleInfo from "../toggle-info";
import ActionsMenu from "../../actions-menu";
import PhotosModal from "../../modals/slider/PhotosModal";
import RouteMapModal from "../../modals/route-map/RouteMapModal";
import LoadInfoModal from "../../modals/load-info-modal";
import { getFileUrlRelative } from "../../../utils/fileUrl";
import loadService from "../../../services/loadService";
import "./Item.scss";

const Item = ({
  item,
  itemType, // 'loads', 'users', 'customers', 'carriers', 'payments'
  isSelected,
  onSelect,
  onToggleExpand,
  onDelete,
  onEdit,
  onChangeStatus,
  onViewFiles,
  canSelect = true,
  canDelete = true,
  canEdit = true,
  canChangeStatus = true,
  canDuplicate = true,
  expanded = false,
  toggleData,
}) => {
  const navigate = useNavigate();
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const [isLoadInfoModalOpen, setIsLoadInfoModalOpen] = useState(false);
  const [isPhotosModalOpen, setIsPhotosModalOpen] = useState(false);
  const [photosModalTitle, setPhotosModalTitle] = useState("");
  const [photosModalImages, setPhotosModalImages] = useState([]);
  const [isRouteMapOpen, setIsRouteMapOpen] = useState(false);
  const actionsMenuTriggerRef = useRef(null);
  const formatDate = (date) => {
    if (!date) return "--";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Parse currency string (e.g., "$1,000.00") or number to float
  const parseCurrency = (value) => {
    if (typeof value === 'number') return value;
    if (!value || value === 'N/A' || value === '--') return 0;
    // Remove $, commas, and whitespace, then parse
    const cleaned = String(value).replace(/[$,\s]/g, '');
    return parseFloat(cleaned) || 0;
  };

  // Handle actions menu toggle
  const handleActionsMenuToggle = () => {
    setIsActionsMenuOpen(!isActionsMenuOpen);
  };

  // Handle edit action
  const handleEdit = () => {
    if (onEdit) {
      onEdit(item);
    } else {
      // Default navigation
      if (itemType === "loads") {
        navigate(`/loads/edit/${item._id || item.id}`);
      } else if (itemType === "users") {
        navigate(`/users/edit/${item._id || item.id}`);
      } else if (itemType === "customers") {
        navigate(`/customers/edit/${item._id || item.id}`);
      } else if (itemType === "carriers") {
        navigate(`/carriers/edit/${item._id || item.id}`);
      }
    }
    setIsActionsMenuOpen(false);
  };

  // Handle view images for loads
  // Note: Server already converts S3 keys to signed URLs, so we use URLs directly
  const resolveImageUrl = (img) => {
    if (typeof img !== 'string') {
      const url = img?.url || img;
      if (typeof url === 'string' && !url.startsWith('http') && !url.startsWith('blob:')) {
        return `/files/${url.replace(/^\/api\/files\//, '').replace(/^\/files\//, '')}`;
      }
      return url;
    }
    if (img.startsWith('http') || img.startsWith('blob:')) return img;
    const key = img.replace(/^\/api\/files\//, '').replace(/^\/files\//, '');
    return `/files/${key}`;
  };

  const handleViewVehicleImages = () => {
    const load = item.load || item;
    const vehicleImages = (load.vehicle?.vehicleImages || []).filter(
      (img) => img && (typeof img === "string" ? img.trim() : img.url || img)
    );
    const imageObjects = vehicleImages
      .map((img, index) => {
        return {
          id: img.id || `vehicle-${index}`,
          url: resolveImageUrl(img),
          alt: img.alt || `Vehicle image ${index + 1}`,
        };
      })
      .filter((obj) => obj.url); // Filter out any objects without valid URLs
    setPhotosModalTitle("Vehicle Photos");
    setPhotosModalImages(imageObjects);
    setIsPhotosModalOpen(true);
    setIsActionsMenuOpen(false);
  };

  const handleViewFreightImages = () => {
    const load = item.load || item;
    const freightImages = (load.freight?.freightImages || []).filter(
      (img) => img && (typeof img === "string" ? img.trim() : img.url || img)
    );
    const imageObjects = freightImages
      .map((img, index) => {
        return {
          id: img.id || `freight-${index}`,
          url: resolveImageUrl(img),
          alt: img.alt || `Freight image ${index + 1}`,
        };
      })
      .filter((obj) => obj.url); // Filter out any objects without valid URLs
    setPhotosModalTitle("Freight Photos");
    setPhotosModalImages(imageObjects);
    setIsPhotosModalOpen(true);
    setIsActionsMenuOpen(false);
  };

  const handleViewPickupImages = () => {
    const load = item.load || item;
    const pickupImages = (load.pickup?.images || []).filter(
      (img) => img && (typeof img === "string" ? img.trim() : img.url || img)
    );
    const imageObjects = pickupImages
      .map((img, index) => {
        return {
          id: img.id || `pickup-${index}`,
          url: resolveImageUrl(img),
          alt: img.alt || `Pickup image ${index + 1}`,
        };
      })
      .filter((obj) => obj.url); // Filter out any objects without valid URLs
    setPhotosModalTitle("Pickup Photos");
    setPhotosModalImages(imageObjects);
    setIsPhotosModalOpen(true);
    setIsActionsMenuOpen(false);
  };

  const handleViewDeliveryImages = () => {
    const load = item.load || item;
    const deliveryImages = (load.delivery?.images || []).filter(
      (img) => img && (typeof img === "string" ? img.trim() : img.url || img)
    );
    const imageObjects = deliveryImages
      .map((img, index) => {
        return {
          id: img.id || `delivery-${index}`,
          url: resolveImageUrl(img),
          alt: img.alt || `Delivery image ${index + 1}`,
        };
      })
      .filter((obj) => obj.url); // Filter out any objects without valid URLs
    setPhotosModalTitle("Delivery Photos");
    setPhotosModalImages(imageObjects);
    setIsPhotosModalOpen(true);
    setIsActionsMenuOpen(false);
  };

  // Handle view route for loads
  const handleViewRoute = () => {
    setIsRouteMapOpen(true);
    setIsActionsMenuOpen(false);
  };

  // Handle status change
  const handleStatusChange = () => {
    if (onChangeStatus) {
      const currentStatus = item.status?.toLowerCase();
      let newStatus;

      if (
        itemType === "carriers" ||
        itemType === "users" ||
        itemType === "customers"
      ) {
        // For carriers, users, and customers: toggle between active and inactive/suspended
        if (currentStatus === "active") {
          newStatus = "inactive";
        } else {
          // If inactive or suspended, activate
          newStatus = "active";
        }
      } else {
        // Default behavior for other types
        newStatus = currentStatus === "active" ? "inactive" : "active";
      }

      onChangeStatus(item._id || item.id, newStatus);
    }
    setIsActionsMenuOpen(false);
  };

  const handleInfo = () => {
    setIsLoadInfoModalOpen(true);
    setIsActionsMenuOpen(false);
  };

  const handleDuplicateLoad = async () => {
    try {
      setIsActionsMenuOpen(false);
      const loadId = item?._id || item?.id || item?.load?._id || item?.load?.id;
      if (!loadId) return;
      const response = await loadService.duplicateLoad(loadId);
      const newLoad = response?.data?.data || response?.data?.load || response?.data;
      const newId = newLoad?._id || newLoad?.id;
      if (newId) {
        navigate(`/loads/edit/${newId}`);
      }
    } catch (error) {
      console.error("Failed to duplicate load:", error);
    }
  };
  // Get actions for menu based on item type
  const getActions = () => {
    const actions = [];

    if (itemType === "loads") {
      const load = item.load || item;
      const vehicleImages = load.vehicle?.vehicleImages || [];
      const freightImages = load.freight?.freightImages || [];
      const pickupImages = load.pickup?.images || [];
      const deliveryImages = load.delivery?.images || [];
      const hasVehicleImages = vehicleImages.length > 0;
      const hasFreightImages = freightImages.length > 0;
      const hasPickupImages = pickupImages.length > 0;
      const hasDeliveryImages = deliveryImages.length > 0;

      if (canEdit) {
        actions.push({
          label: "Edit",
          onClick: handleEdit,
        });
      }

   
      // Pickup Photos
      if (hasPickupImages) {
        actions.push({
          label: "View Pickup Photos",
          onClick: handleViewPickupImages,
        });
      }

      // Delivery Photos
      if (hasDeliveryImages) {
        actions.push({
          label: "View Delivery Photos",
          onClick: handleViewDeliveryImages,
        });
      }

      // Vehicle Photos
      if (hasVehicleImages) {
        actions.push({
          label: "View Vehicle Photos",
          onClick: handleViewVehicleImages,
        });
      }

      // Freight Photos
      if (hasFreightImages) {
        actions.push({
          label: "View Freight Photos",
          onClick: handleViewFreightImages,
        });
      }

      // Shipment Photos (combined Vehicle + Freight)
      if (hasVehicleImages && hasFreightImages) {
        actions.push({
          label: "View Shipment Photos",
          onClick: () => {
            // Server already provides signed URLs, use them directly
            // Filter out invalid images first
            const validVehicleImages = vehicleImages.filter(
              (img) =>
                img && (typeof img === "string" ? img.trim() : img.url || img)
            );
            const validFreightImages = freightImages.filter(
              (img) =>
                img && (typeof img === "string" ? img.trim() : img.url || img)
            );

            const allImages = [
              ...validVehicleImages.map((img, index) => {
                if (typeof img === "string") {
                  return {
                    id: `vehicle-${index}`,
                    url: img, // Use URL directly (already signed URL from server)
                    type: "vehicle",
                    alt: `Vehicle image ${index + 1}`,
                  };
                }
                return {
                  id: img.id || `vehicle-${index}`,
                  url: img.url || img,
                  type: "vehicle",
                  alt: img.alt || `Vehicle image ${index + 1}`,
                };
              }),
              ...validFreightImages.map((img, index) => {
                if (typeof img === "string") {
                  return {
                    id: `freight-${index}`,
                    url: img, // Use URL directly (already signed URL from server)
                    type: "freight",
                    alt: `Freight image ${index + 1}`,
                  };
                }
                return {
                  id: img.id || `freight-${index}`,
                  url: img.url || img,
                  type: "freight",
                  alt: img.alt || `Freight image ${index + 1}`,
                };
              }),
            ].filter((obj) => obj.url); // Filter out any objects without valid URLs
            setPhotosModalTitle("Shipment Photos");
            setPhotosModalImages(allImages);
            setIsPhotosModalOpen(true);
            setIsActionsMenuOpen(false);
          },
        });
      }
      if (canDuplicate) {
        actions.push({
          label: "Duplicate Load",
          onClick: handleDuplicateLoad,
        });
      }

      // View Files action - shows only PDF files (without slider)
      const hasAnyFiles =
        (load.pdfs && load.pdfs.length > 0) ||
        (load.documents && load.documents.length > 0) ||
        (load.vehicle?.pdfs && load.vehicle.pdfs.length > 0) ||
        (load.freight?.pdfs && load.freight.pdfs.length > 0) ||
        load.bolPdfPath ||
        load.rateConfirmationPdfPath;

      actions.push({
        label: "View Files",
        onClick: () => {
          setIsActionsMenuOpen(false);
          if (onViewFiles) {
            onViewFiles(item);
          }
        },
        disabled: !hasAnyFiles,
      });
      if (load)
        actions.push({
          label: "More Details",
          onClick: handleInfo,
        });
    } else if (itemType === "users") {
      if (canEdit) {
        actions.push({
          label: "Edit",
          onClick: handleEdit,
        });
      }

      // View Files action
      const hasFiles = item.pdfs && item.pdfs.length > 0;
      actions.push({
        label: "View Files",
        onClick: () => {
          setIsActionsMenuOpen(false);
          if (onViewFiles) {
            onViewFiles(item);
          }
        },
        disabled: !hasFiles,
      });

      // Status change action
      const currentStatus = item.status?.toLowerCase();
      const isActive = currentStatus === "active";
      if (canChangeStatus) {
        actions.push({
          label: isActive ? "Disable" : "Activate",
          onClick: handleStatusChange,
        });
      }
    } else if (itemType === "carriers") {
      if (canEdit) {
        actions.push({
          label: "Edit",
          onClick: handleEdit,
        });
      }

      // View Files action
      const hasFilesCarrier = item.pdfs && item.pdfs.length > 0;
      actions.push({
        label: "View Files",
        onClick: () => {
          setIsActionsMenuOpen(false);
          if (onViewFiles) {
            onViewFiles(item);
          }
        },
        disabled: !hasFilesCarrier,
      });

      // Status change action
      const currentStatusCarrier = item.status?.toLowerCase();
      const isActiveCarrier = currentStatusCarrier === "active";
      if (canChangeStatus) {
        actions.push({
          label: isActiveCarrier ? "Disable" : "Activate",
          onClick: handleStatusChange,
        });
      }
    } else if (itemType === "customers") {
      if (canEdit) {
        actions.push({
          label: "Edit",
          onClick: handleEdit,
        });
      }

      // View Files action
      const hasFilesCustomer = item.pdfs && item.pdfs.length > 0;
      actions.push({
        label: "View Files",
        onClick: () => {
          setIsActionsMenuOpen(false);
          if (onViewFiles) {
            onViewFiles(item);
          }
        },
        disabled: !hasFilesCustomer,
      });

      // Status change action
      const currentStatusCustomer = item.status?.toLowerCase();
      const isActiveCustomer = currentStatusCustomer === "active";
      if (canChangeStatus) {
        actions.push({
          label: isActiveCustomer ? "Disable" : "Activate",
          onClick: handleStatusChange,
        });
      }
    } else if (
      itemType === "paymentsReceivable" ||
      itemType === "paymentsPayable"
    ) {
      if (canEdit) {
        actions.push({
          label: "Edit",
          onClick: handleEdit,
        });
      }
      // View Files - показывает прикреплённые PDF файлы
      const hasFiles =
        (item.pdfs && item.pdfs.length > 0) ||
        (item.images && item.images.length > 0);
      actions.push({
        label: "View Files",
        onClick: () => {
          setIsActionsMenuOpen(false);
          if (onViewFiles) {
            onViewFiles(item);
          }
        },
        disabled: !hasFiles,
      });
    }

    return actions;
  };

  const getStatusColor = (status, itemType) => {
    if (itemType === "loads") {
      switch (status?.toLowerCase()) {
        case "delivered":
          return "status-delivered";
        case "picked up":
          return "status-pending";
        case "dispatched":
          return "status-pending";
        case "on hold":
          return "status-pending";
        case "cancelled":
          return "status-cancelled";
        case "listed":
          return "status-default";
        default:
          return "status-default";
      }
    } else if (
      itemType === "users" ||
      itemType === "carriers" ||
      itemType === "customers"
    ) {
      switch (status?.toLowerCase()) {
        case "active":
          return "status-active";
        case "suspended":
        case "inactive":
          return "status-suspended";
        default:
          return "status-default";
      }
    } else if (
      itemType === "paymentsReceivable" ||
      itemType === "paymentsPayable"
    ) {
      switch (status?.toLowerCase()) {
        case "paid":
        case "payed":
          return "status-delivered"; // Зеленый как delivered
        case "received":
          return "status-delivered"; // Зеленый для receivable
        case "partial":
        case "partially paid":
        case "partially received":
          return "status-pending"; // Оранжевый как pending
        case "pending":
          return "status-pending"; // Оранжевый
        case "overdue":
          return "status-cancelled"; // Красный как cancelled
        case "invoiced":
          return "status-invoiced"; // Фиолетовый
        case "withheld":
          return "status-pending"; // Оранжевый
        case "canceled":
        case "cancelled":
          return "status-cancelled"; // Красный
        case "pay today":
          return "status-pending"; // Оранжевый
        case "scheduled":
          return "status-pending"; // Оранжевый
        case "on hold":
        case "onhold":
          return "status-pending"; // Оранжевый как pending
        default:
          return "status-default";
      }
    }
    // Customers don't have status field
    return "status-default";
  };

  const renderItemContent = () => {
    switch (itemType) {
      case "loads":
        // Support both old and new structure
        const load = item.load || item;
        const vehicleShipments = load.vehicle?.shipment || [];
        const freightShipments = load.freight?.shipment || [];

        // Show orderId in main row (VIN only in toggle menu)
        const displayOrderId = load.orderId || item.orderId || "--";

        // Determine type display
        const hasVehicle = vehicleShipments.length > 0;
        const hasFreight = freightShipments.length > 0;
        let typeDisplay = "--";
        if (hasVehicle && hasFreight) {
          typeDisplay = `Vehicle (${vehicleShipments.length}), Freight (${freightShipments.length})`;
        } else if (hasVehicle) {
          typeDisplay =
            vehicleShipments.length > 1
              ? `Vehicle (${vehicleShipments.length})`
              : "Vehicle";
        } else if (hasFreight) {
          typeDisplay =
            freightShipments.length > 1
              ? `Freight (${freightShipments.length})`
              : "Freight";
        } else if (load.type) {
          // Fallback to old structure
          if (load.type.vehicle && load.type.freight) {
            typeDisplay = "Vehicle, Freight";
          } else if (load.type.vehicle) {
            typeDisplay = "Vehicle";
          } else if (load.type.freight) {
            typeDisplay = "Freight";
          }
        } else if (item.type) {
          typeDisplay = item.type;
        }

        // Get date from new structure
        const displayDate =
          load.pickup?.date ||
          load.dates?.pickupDate ||
          item.pickUpDate ||
          item.createdAt;

        return (
          <>
            <div className="item-vin">{displayOrderId}</div>
            <div className="item-date">{formatDate(displayDate)}</div>
            <div className="item-type">{typeDisplay}</div>
            <div className="item-status">
              <span
                className={`status-badge ${getStatusColor(
                  load.status || item.status,
                  itemType
                )}`}
              >
                {load.status || item.status || "Listed"}
              </span>
            </div>
            <div className="item-more">
              <button
                ref={actionsMenuTriggerRef}
                type="button"
                className="action-btn"
                title="More options"
                onClick={handleActionsMenuToggle}
              >
                <CustomIcon name="moreb" size={24} />
              </button>
            </div>
            <div className={`item-expand ${expanded ? "expanded" : ""}`}>
              <button
                type="button"
                className="toggle-btn"
                onClick={() => onToggleExpand(item._id || item.id)}
                title={expanded ? "Hide details" : "Show details"}
              >
                <CustomIcon name={"down"} type="svg" size={32} />
              </button>
            </div>
          </>
        );

      case "users":
        const fullName =
          item.firstName && item.lastName
            ? `${item.firstName} ${item.lastName}`
            : item.fullName || item.name || "Unknown User";
        const initials =
          item.firstName && item.lastName
            ? `${item.firstName.charAt(0)}${item.lastName.charAt(
                0
              )}`.toUpperCase()
            : (item.fullName || item.name || "U").charAt(0).toUpperCase();

        return (
          <>
            <div className="item-avatar">
              {item.profileImage ? (
                <img src={item.profileImage} alt={fullName} />
              ) : (
                <div className="avatar-placeholder">{initials}</div>
              )}
            </div>
            <div className="item-name">{fullName}</div>
            <div className="item-email">{item.email || "--"}</div>
            <div className="item-phone">{item.phoneNumber || "--"}</div>
            <div className="item-company">{item.companyName || "--"}</div>
            <div className="item-status">
              <span
                className={`status-badge ${getStatusColor(
                  item.status,
                  itemType
                )}`}
              >
                {item.status && item.status !== "active"
                  ? "DNT"
                  : item.status.charAt(0).toUpperCase() + item.status.slice(1)}
              </span>
            </div>
            <div className="item-actions">
              <button
                ref={actionsMenuTriggerRef}
                type="button"
                className="action-btn"
                title="More options"
                onClick={handleActionsMenuToggle}
              >
                <CustomIcon name="moreb" size={24} />
              </button>
            </div>
          </>
        );

      case "customers":
        // Customer model: companyName, customerAddress, emails, phoneNumber, status
        const customerName =
          item.companyName || item.name || "Unknown Customer";
        const customerEmail = item.email || item.emails?.[0] || "--";

        return (
          <>
            <div className="item-name">{customerName}</div>
            <div className="item-email">{customerEmail}</div>
            <div className="item-status">
              <span
                className={`status-badge ${getStatusColor(
                  item.status,
                  itemType
                )}`}
              >
                {item.status
                  ? item.status === "active"
                    ? "Active"
                    : item.status === "suspended"
                    ? "Suspended"
                    : "DNT"
                  : "Active"}
              </span>
            </div>
            <div className="item-more">
              <button
                ref={actionsMenuTriggerRef}
                type="button"
                className="action-btn"
                title="More options"
                onClick={handleActionsMenuToggle}
              >
                <CustomIcon name="moreb" size={24} />
              </button>
            </div>
            <div className={`item-expand ${expanded ? "expanded" : ""}`}>
              <button
                type="button"
                className="toggle-btn"
                onClick={() => onToggleExpand(item._id || item.id)}
                title={expanded ? "Hide details" : "Show details"}
              >
                <CustomIcon name={"down"} type="svg" size={32} />
              </button>
            </div>
          </>
        );

      case "carriers":
        const location =
          item.city && item.state
            ? `${item.city}, ${item.state}`
            : item.location || "--";

        return (
          <>
            <div className="item-name">{item.companyName || "--"}</div>
            <div className="item-dba">{item.dba || "--"}</div>
            <div className="item-mc">{item.mcNumber || "--"}</div>
            <div className="item-dot">{item.dotNumber || "--"}</div>
            <div className="item-location">{location}</div>
            <div className="item-status">
              <span
                className={`status-badge ${getStatusColor(
                  item.status,
                  itemType
                )}`}
              >
                {item.status
                  ? item.status === "active"
                    ? "Active"
                    : item.status === "suspended"
                    ? "Suspended"
                    : "DNT"
                  : "Active"}
              </span>
            </div>
            <div className="item-more">
              <button
                ref={actionsMenuTriggerRef}
                type="button"
                className="action-btn"
                title="More options"
                onClick={handleActionsMenuToggle}
              >
                <CustomIcon name="moreb" size={24} />
              </button>
            </div>
            <div className={`item-expand ${expanded ? "expanded" : ""}`}>
              <button
                type="button"
                className="toggle-btn"
                onClick={() => onToggleExpand(item._id || item.id)}
                title={expanded ? "Hide details" : "Show details"}
              >
                <CustomIcon name={"down"} type="svg" size={32} />
              </button>
            </div>
          </>
        );

      case "payments":
        return (
          <>
            <div className="item-vin">{item.vin || "--"}</div>
            <div className="item-amount">{item.amount}</div>
            <div className="item-type">{item.type}</div>
            <div className="item-status">
              <span
                className={`status-badge ${getStatusColor(
                  item.status,
                  itemType
                )}`}
              >
                {item.status}
              </span>
            </div>
            <div className="item-action">
              <button type="button" className="action-btn" title="Edit">
                <CustomIcon name="edit-03" size={20} />
              </button>
            </div>
          </>
        );

      case "paymentsReceivable":
        // Calculate total amount: customerRate + fees + tonu
        const baseCustomerRate = parseCurrency(item.load?.customerRate ?? item.customerRate);
        const feesCustomerTotal = Array.isArray(item.fees) && item.fees.length > 0
          ? item.fees.reduce((sum, fee) => sum + parseCurrency(fee.customerRate || fee.total || 0), 0)
          : 0;
        const tonuCustomerRate = (item.tonu?.enabled && item.tonu?.customerRate) 
          ? parseCurrency(item.tonu.customerRate) 
          : 0;
        const calculatedTotalReceivable = baseCustomerRate + feesCustomerTotal + tonuCustomerRate;
        
        // Use totalAmount from server if available, otherwise calculate
        const totalAmountReceivableValue = item.totalAmount 
          ? parseCurrency(item.totalAmount) 
          : calculatedTotalReceivable;
        
        // Format amounts
        const customerRateFormatted = baseCustomerRate > 0
          ? `$${baseCustomerRate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : "--";
        const totalAmountReceivable = totalAmountReceivableValue > 0
          ? `$${totalAmountReceivableValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : customerRateFormatted;
        
        return (
          <>
            <div className="item-order-id">{item.orderId || "--"}</div>
            <div className="item-customer-rate">
              {customerRateFormatted}
            </div>
            <div className="item-total-amount">
              {totalAmountReceivable}
            </div>
            <div className="item-status">
              <span
                className={`status-badge ${getStatusColor(
                  item.status,
                  itemType
                )}`}
              >
                {item.status || "Pending"}
              </span>
            </div>
            <div className="item-days-on-hold">
              <span className={item.daysOnHoldRaw > 30 ? "overdue" : ""}>
                {item.daysOnHold || "—"}
              </span>
            </div>
            <div className="item-past-due">
              {item.isPastDue && item.pastDue ? (
                <span
                  className="past-due-badge"
                  style={{ color: "#ef4444", fontWeight: "bold" }}
                >
                  {item.pastDue}
                </span>
              ) : (
                <span style={{ color: "#9ca3af" }}>—</span>
              )}
            </div>
            <div className="item-more">
              <button
                ref={actionsMenuTriggerRef}
                type="button"
                className="action-btn"
                title="More options"
                onClick={handleActionsMenuToggle}
              >
                <CustomIcon name="moreb" size={20} />
              </button>
            </div>
            <div className={`item-expand ${expanded ? "expanded" : ""}`}>
              <button
                type="button"
                className="toggle-btn"
                onClick={() => onToggleExpand(item._id || item.id)}
                title={expanded ? "Hide details" : "Show details"}
              >
                <CustomIcon name={"down"} type="svg" size={32} />
              </button>
            </div>
          </>
        );

      case "paymentsPayable":
        // Calculate total amount: carrierRate + fees + tonu

        const baseCarrierRate = parseCurrency(item.load?.carrierRate ?? item.carrierRate);
        const feesCarrierTotal = Array.isArray(item.fees) && item.fees.length > 0
          ? item.fees.reduce((sum, fee) => sum + parseCurrency(fee.carrierRate || fee.total || 0), 0)
          : 0;
        const tonuCarrierRate = (item.tonu?.enabled && item.tonu?.carrierRate) 
          ? parseCurrency(item.tonu.carrierRate) 
          : 0;
        const calculatedTotalPayable = baseCarrierRate + feesCarrierTotal + tonuCarrierRate;
        
        // Use totalAmount from server if available, otherwise calculate
        const totalAmountPayableValue = item.totalAmount 
          ? parseCurrency(item.totalAmount) 
          : calculatedTotalPayable;
        // Format amounts
        const carrierRateFormatted = baseCarrierRate > 0
          ? `$${baseCarrierRate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : "N/A";
        const totalAmountPayable = totalAmountPayableValue > 0
          ? `$${totalAmountPayableValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : carrierRateFormatted;
        const carrierName = item.carrier?.companyName || item.carrierCompanyName || "N/A";

        
        return (
          <>
            <div className="item-company-name">
              {carrierName}
            </div>
            <div className="item-order-id">{item.orderId || "--"}</div>
            <div className="item-carrier-rate">{carrierRateFormatted}</div>
            <div className="item-total-amount">
              {totalAmountPayable}
            </div>
            <div className="item-status">
              <span
                className={`status-badge ${getStatusColor(
                  item.status,
                  itemType
                )}`}
              >
                {item.status || "Pending"}
              </span>
            </div>
            <div className="item-days-on-hold">
              <span className={item.daysOnHoldRaw > 30 ? "overdue" : ""}>
                {item.daysOnHold || "—"}
              </span>
            </div>
            <div className="item-more">
              <button
                ref={actionsMenuTriggerRef}
                type="button"
                className="action-btn"
                title="More options"
                onClick={handleActionsMenuToggle}
              >
                <CustomIcon name="moreb" size={20} />
              </button>
            </div>
            <div className={`item-expand ${expanded ? "expanded" : ""}`}>
              <button
                type="button"
                className="toggle-btn"
                onClick={() => onToggleExpand(item._id || item.id)}
                title={expanded ? "Hide details" : "Show details"}
              >
                <CustomIcon name={"down"} type="svg" size={32} />
              </button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const actions = getActions();

  return (
    <>
      <div
        className={`item-list-item ${itemType} ${expanded ? "expanded" : ""} ${
          isActionsMenuOpen ? "actions-menu-open" : ""
        }`}
      >
        {/* Main Row */}
        <div className={`item-main-row ${itemType}`}>
          <div className="item-checkbox">
            {canSelect && (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect(item._id || item.id)}
                className="item-checkbox-input"
              />
            )}
          </div>
          {renderItemContent()}
          {/* Empty delete column placeholder to match header structure */}
          <div className="item-delete"></div>
        </div>
        {/* Expanded Details */}
        {expanded && (
          <ToggleInfo
            item={item}
            itemType={itemType}
            toggleData={toggleData}
            expanded={expanded}
          />
        )}

        {/* Actions Menu - positioned relative to item */}
        {actions.length > 0 && (
          <ActionsMenu
            isOpen={isActionsMenuOpen}
            onClose={() => setIsActionsMenuOpen(false)}
            triggerRef={actionsMenuTriggerRef}
            actions={actions}
            position="bottom-right"
            relativeToItem={true}
          />
        )}
      </div>

      {/* Photos Modal for Loads */}
      {itemType === "loads" && (
        <>
          <LoadInfoModal
            isOpen={isLoadInfoModalOpen}
            onClose={() => setIsLoadInfoModalOpen(false)}
            loadData={item.load || item}
          />
          <PhotosModal
            isOpen={isPhotosModalOpen}
            onClose={() => setIsPhotosModalOpen(false)}
            photos={photosModalImages}
            title={photosModalTitle}
          />
          <RouteMapModal
            isOpen={isRouteMapOpen}
            onClose={() => setIsRouteMapOpen(false)}
            pickup={(item.load || item).pickup}
            delivery={(item.load || item).delivery}
          />
        </>
      )}
    </>
  );
};

export default Item;
